// 게임 안 캐릭터 진입점(docs/CHARACTER-PRD.md §3.4): 타이틀·마을 HUD의 「캐릭터」 버튼이 여는 선택 화면과, 버튼에 쓸 지금 캐릭터 이름.
// main.ts가 캐릭터 모드(bootCharacters → active)일 때만 만든다(꺼져 있으면 null — 마을 HUD는 지금처럼 「영웅」 고르기).
// 던전·레이드 안에서는 열지 않는다(버튼이 타이틀·마을에만 있다). 다른 캐릭터를 고르면 게임 저장을 쓰고 새로고침한다(저장 범위는 부팅 때 정해진다).
import { CharacterScreen } from './characterScreen';
import { CLASS_LABEL, currentCharacter, formatHandle } from '../characters/index';

export interface CharacterEntry {
  /** 버튼 글자(「이름#태그」 · 이름이 없으면 「이름 정하기」) */
  label(): string;
  /** 지금 캐릭터 직업 한 줄(「검사」) */
  classText(): string;
  /** 지금 캐릭터에 이름이 없다(이관 캐릭터 — 첫 접속에 정한다) */
  unnamed(): boolean;
  open(notice?: string): void;
  isOpen(): boolean;
  /** 화면을 닫은 뒤(이름이 바뀌었을 수 있다) 버튼 글자를 다시 읽게 알린다 */
  onChange(f: () => void): void;
}

let entry: CharacterEntry | null = null;
/** HUD·타이틀이 읽는다(캐릭터 모드가 아니면 null) */
export function characterEntry(): CharacterEntry | null { return entry; }

export function mountCharacterEntry(parent: HTMLElement, hooks: { flush(): void; reload(): void }): CharacterEntry {
  let screen: CharacterScreen | null = null;
  const subs: (() => void)[] = [];
  let cache: { label: string; cls: string; unnamed: boolean } | null = null;
  const read = (): { label: string; cls: string; unnamed: boolean } => {
    if (cache) return cache;
    const c = currentCharacter();
    const unnamed = !c || !c.name || c.tag === null;
    cache = { label: !c ? '캐릭터' : unnamed ? '이름 정하기' : formatHandle(c.name!, c.tag!), cls: c ? CLASS_LABEL[c.cls] : '', unnamed: !!c && unnamed };
    return cache;
  };
  const changed = (): void => { cache = null; for (const f of subs) { try { f(); } catch { /* UI 오류가 다른 구독을 막지 않게 */ } } };
  entry = {
    label: () => read().label,
    classText: () => read().cls,
    unnamed: () => read().unnamed,
    isOpen: () => !!screen?.isOpen,
    onChange: (f) => { subs.push(f); },
    open: (notice) => {
      screen ??= new CharacterScreen(parent, {
        inGame: true,
        play: () => { try { hooks.flush(); } catch { /* 저장 실패여도 고른 캐릭터로 간다(지금 캐릭터 진행은 이미 자동 저장) */ } hooks.reload(); },
        closed: changed,
      });
      screen.open('list', notice);
    },
  };
  return entry;
}

/**
 * 부팅 선택 화면(게임을 만들기 전 — 만들면 원래 키에 쓰게 된다). 계정 세션·판정은 계정 클라이언트가 한다(목록·생성은 세션이 있어야 한다).
 *  - select: 지금 캐릭터가 없다(처음 만든 계정·지금 캐릭터 삭제·다른 기기 캐릭터 받는 중) → 목록·생성. 고르면 새로고침 → 그 캐릭터로 부팅
 *  - preparing: 이 기기에 원래 진행이 없고 이관 판정 전(새 기기) → 「준비 중」. 계정 클라이언트가 판정(이관 없음·서버 캐릭터 받기)을 끝내고 새로고침하면 select로.
 *    서버에 닿지 않으면 「서버 없이 시작」을 고를 수 있고, 그때 돌려주는 약속이 'legacy'로 풀린다(호출자가 원래 키로 게임을 만든다)
 */
export function startCharacterSelect(parent: HTMLElement, account: (hooks: { isSafe(): boolean; flush(): void; reload(): void; setBusy?(b: boolean): void }) => void, opts: { setBusy?: (b: boolean) => void; preparing?: boolean } = {}): Promise<'legacy'> {
  return new Promise((resolve) => {
    let offline = false;
    const screen = new CharacterScreen(parent, {
      inGame: false,
      play: () => location.reload(),
      preparing: opts.preparing ? { offline: () => { offline = true; resolve('legacy'); } } : undefined,
    });
    // 서버 진행 불러오기·이관 판정 새로고침은 화면이 쉬고 있을 때만(입력·요청 중에는 미룬다). 「서버 없이 시작」 뒤에는 게임 훅으로 바뀐다
    account({ isSafe: () => !offline && screen.idle(), flush: () => {}, reload: () => location.reload(), setBusy: opts.setBusy });
    screen.open('list');
  });
}

/** 이관 직후(지금 캐릭터에 이름이 없다) 타이틀에서 한 번 여는 안내 */
export const NAME_NOTICE = '기존 진행을 캐릭터로 옮겼어요. 이름을 정하면 채팅·친구·레이드에 그 이름이 보여요(나중에 정해도 돼요)';
